<template>
  <TopBarForDrawer :is-white-background="isWhiteBackground">
    <template #page-title>
      <slot
        name="page-title"
        :library-loading="libraryLoading"
        :page-description="description"
      >
        <TopBarForDrawerTitle
          :is-loading="libraryLoading"
          :page-title="route.meta.title"
          :page-description="description"
        />
      </slot>
    </template>

    <template #summaries>
      <slot
        name="summaries"
        :library-loading="libraryLoading"
        :totals="totals"
      >
        <DocumentLibrarySummary
          v-if="route.name !== 'templates'"
          :is-loading="libraryLoading"
          :totals="totals"
          :document-type="route.name"
        />
      </slot>
    </template>

    <template #topbar-actions>
      <TemplateFilterBar
        v-if="route.name === 'templates'"
        :loading="filtersLoading || pageLoading"
      />
      <div v-else class="d-flex flex-column ga-2 w-100">
        <slot
          name="above-filters"
          :library-loading="libraryLoading"
          :totals="totals"
        />
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center ga-2 w-100">
            <FiltersDrawerToggle
              v-if="pageHasFilters[route.name]"
              v-model="openDrawer"
              :is-disabled="libraryLoading"
              :is-loading="pageLoading"
              :are-filters-applied="areNonIgnorableFiltersApplied"
            />
            <slot name="next-to-filters" />
            <SearchButton
              v-if="route.name !== 'federal-flsa-dashboard'
                && route.name !== 'employee-acknowledgements'
                && route.name !== 'approvals-library'"
              :is-disabled="libraryLoading"
            />
          </div>

          <slot
            name="actions-trailing"
            :library-loading="libraryLoading"
          />
        </div>
      </div>
    </template>
  </TopBarForDrawer>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import DocumentLibrarySummary from '@/components/library/DocumentLibrarySummary.vue';
import SearchButton from '@/components/filter/filterMenu/SearchButton.vue';
import TemplateFilterBar from '@/components/library/templates/TemplateFilterBar.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import FiltersDrawerToggle from '@/components/filter/FiltersDrawerToggle.vue';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';

// Router
const route = useRoute();

// Stores
const appLoadingStore = useAppLoading();
const { pageLoading, listLoading, filtersLoading } = storeToRefs(appLoadingStore);

const appBaseStore = useAppBase();
const { pageHasFilters } = storeToRefs(appBaseStore);

const filtersStore = useFilters();
const { areNonIgnorableFiltersApplied } = storeToRefs(filtersStore);

const props = defineProps({
  totals: { type: Object, default: () => {} },
  modelValue: { type: Boolean, default: false },
  isWhiteBackground: { type: Boolean, default: false },
});

const emit = defineEmits(['update:model-value']);

const openDrawer = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:model-value', value),
});

// Computed
const libraryLoading = computed(
  () => pageLoading.value || listLoading.value,
);

const description = computed(() => pageDescriptions()[route.name] || '');
</script>

<style lang="less" scoped>
:deep(.lever-message-activator) {
  margin: 2px 8px 0px 8px;
}

</style>
